import React, { useCallback, useEffect, useRef, useState } from "react";
import { recordView } from "@/lib/recent";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin, CalendarCheck, Lock, Link2, Smartphone, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { stageApi, compact, OPEN_TO_LABEL } from "@/lib/stageApi";
import { worldOf } from "@/lib/worlds";
import { CreatorAvatar, VerifiedMark, AiTag, PostTile, PostCard } from "@/components/stage/PostCard";
import { GiftButton } from "@/pages/account/Money";
import { LiveBadge, ProfileActivity, ReportButton } from "@/pages/stage/Social";

const TAB_LABEL = { posts: "Posts", reels: "Reels", pulse: "Pulse", reshares: "Reshares", tagged: "Tagged", collabs: "Collabs" };

function StatBlock({ n, label }) {
  return (
    <div className="text-center sm:text-left">
      <p className="font-mono-num text-xl font-bold leading-none">{compact(n)}</p>
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      <Skeleton className="h-48 rounded-3xl" />
      <div className="mt-4 flex items-center gap-4">
        <Skeleton className="h-24 w-24 rounded-full" />
        <div className="flex-1 space-y-2"><Skeleton className="h-6 w-48" /><Skeleton className="h-4 w-32" /></div>
      </div>
      <div className="mt-8 grid grid-cols-3 gap-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}</div>
    </div>
  );
}

export default function CreatorProfile({ handle: handleProp }) {
  const { handle: raw } = useParams();
  const handle = (handleProp || raw || "").replace(/^@/, "").toLowerCase();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(undefined);
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState({ items: [], cursor: null, locked: null, state: "loading", pinned: null });
  const [following, setFollowing] = useState(false);
  useEffect(() => { if (profile && profile.handle) recordView({ type: "creator", id: profile.handle, title: profile.display_name || "@" + profile.handle, image: profile.avatar_url, url: "/creators/" + profile.handle }); }, [profile]);
  const [followers, setFollowers] = useState(0);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  usePageMeta(profile ? `${profile.display_name || profile.handle} (@${profile.handle})` : "Creator", profile?.bio || "A creator on Fable + Stage.");

  useEffect(() => {
    let alive = true;
    setProfile(undefined);
    stageApi
      .creator(handle)
      .then((p) => {
        if (!alive) return;
        setProfile(p);
        setFollowing(!!p.viewer?.following);
        setFollowers(p.stats?.followers || 0);
        const first = (p.tabs || []).find((t) => !t.hidden);
        setTab(first ? first.key : "posts");
      })
      .catch((e) => alive && setProfile({ error: e?.status === 404 ? "This creator doesn't exist or isn't public." : e?.message || "Couldn't load this profile." }));
    return () => {
      alive = false;
    };
  }, [handle, user?.id]);

  const loadPosts = useCallback(
    async (cursor) => {
      const my = ++seq.current;
      setPosts((s) => ({ ...s, state: cursor ? "more" : "loading", ...(cursor ? {} : { items: [], locked: null }) }));
      try {
        const d = await stageApi.creatorPosts(handle, { section: tab, cursor: cursor || undefined, limit: 18 });
        if (my !== seq.current) return;
        setPosts((s) => {
          const base = cursor ? s.items : [];
          const seen = new Set(base.map((x) => x.id));
          return { items: [...base, ...(d.items || []).filter((x) => !seen.has(x.id))], cursor: d.next_cursor || null, locked: d.locked || null, pinned: d.pinned || null, state: "idle" };
        });
      } catch (e) {
        if (my !== seq.current) return;
        setPosts((s) => ({ ...s, state: "idle" }));
      }
    },
    [handle, tab],
  );

  useEffect(() => {
    if (profile && !profile.error) loadPosts(null);
  }, [profile, loadPosts]);

  const toggleFollow = async () => {
    if (!user) {
      toast("Sign in to follow creators.");
      navigate("/login", { state: { from: `/@${handle}` } });
      return;
    }
    if (busy) return;
    const next = !following;
    setBusy(true);
    setFollowing(next);
    setFollowers((n) => Math.max(0, n + (next ? 1 : -1)));
    try {
      const r = await stageApi.follow(profile.user_id, next);
      if (r && typeof r.followers === "number") setFollowers(r.followers);
    } catch (e) {
      setFollowing(!next);
      setFollowers((n) => Math.max(0, n + (next ? -1 : 1)));
      toast.error(e.message || "Couldn't update that.");
    } finally {
      setBusy(false);
    }
  };

  if (profile === undefined) return <ProfileSkeleton />;
  if (profile.error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
        <h1 className="font-serif-display text-3xl font-bold">@{handle}</h1>
        <p className="mt-2 text-muted-foreground">{profile.error}</p>
        <Link to="/creators"><Button variant="outline" className="mt-6 rounded-full"><ArrowLeft size={16} className="mr-1.5" /> All creators</Button></Link>
      </div>
    );
  }

  const p = profile;
  const isMe = p.viewer?.is_owner || (user && user.id === p.user_id);
  const tabs = (p.tabs || []).filter((t) => !t.hidden);
  const openTo = (p.open_to || []).filter((k) => k !== "not_open");
  const rateCard = p.rate_card && p.rate_card.visibility === "public" && Array.isArray(p.rate_card.items) ? p.rate_card.items : [];
  const links = Array.isArray(p.links) ? p.links.filter((l) => l && l.url) : [];
  const gridTabs = new Set(["posts", "reels", "tagged", "collabs"]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6" data-testid={`profile-${p.handle}`}>
      <div className="relative h-40 overflow-hidden rounded-3xl bg-muted sm:h-56">
        {p.banner_url ? <img src={p.banner_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full" style={{ background: "linear-gradient(135deg,#FFE1F0,#F3E8FF)" }} />}
      </div>
      <div className="relative z-10 -mt-12 flex flex-col gap-4 px-2 sm:-mt-14 sm:flex-row sm:items-end sm:px-6">
        <div className="rounded-full border-4 border-background bg-background"><CreatorAvatar author={p} size={104} /></div>
        <div className="min-w-0 flex-1 sm:pb-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-serif-display text-2xl font-bold sm:text-3xl">{p.display_name || p.handle}</h1>
            <VerifiedMark author={p} size={20} />
            <AiTag author={p} />
            {p.verification?.label && p.verification.tier >= 1 && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold">{p.verification.label}</span>}
          </div>
          <p className="text-sm text-muted-foreground">@{p.handle}{p.role_label ? ` · ${p.role_label}` : ""}</p>
              <div className="mt-1 flex flex-wrap items-center gap-3"><LiveBadge creatorId={p.user_id} />{!isMe && p.user_id ? <ReportButton type="user" id={p.user_id} label="Report profile" /> : null}</div>
              <ProfileActivity userId={p.user_id} />
        </div>
        <div className="flex gap-2 sm:pb-2">
          {isMe ? (
            <Link to="/creator-studio"><Button variant="outline" className="rounded-full">Edit profile</Button></Link>
          ) : (
            <><Button onClick={toggleFollow} disabled={busy} variant={following ? "outline" : "default"} className="rounded-full px-6" data-testid="follow-btn" aria-pressed={following}>
              {following ? "Following" : "Follow"}
            </Button>
            {p.user_id ? <GiftButton userId={p.user_id} name={p.display_name || p.handle} /> : null}</>
          )}
          {p.book?.items?.length > 0 && (
            <Link to={`/listing/${p.book.items[0].id}`}><Button variant={following ? "default" : "outline"} className="rounded-full"><CalendarCheck size={16} className="mr-1.5" /> Book</Button></Link>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div>
          <div className="flex flex-wrap gap-6 px-2 sm:px-0">
            <StatBlock n={p.stats?.posts} label="posts" />
            <StatBlock n={followers} label="followers" />
            {p.stats?.collabs_completed > 0 && <StatBlock n={p.stats.collabs_completed} label="collabs" />}
          </div>
          {p.bio && <p className="mt-4 max-w-2xl whitespace-pre-wrap text-[15px] leading-relaxed">{p.bio}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {p.location && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {p.show_only_country ? p.country : p.location}</span>}
            {links.slice(0, 3).map((l) => (
              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 font-medium text-primary hover:underline"><Link2 size={14} /> {l.label || l.platform || new URL(l.url).hostname.replace(/^www\./, "")}</a>
            ))}
          </div>
          {(p.categories?.length > 0 || openTo.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(p.categories || []).map((k) => <span key={k} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize">{k}</span>)}
              {openTo.map((k) => <span key={k} className="rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: "#FFE1F0", color: "var(--fable)" }}>Open to {OPEN_TO_LABEL[k] || k}</span>)}
            </div>
          )}

          <div className="mt-8 flex gap-1 overflow-x-auto border-b border-border [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
            {tabs.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`ptab-${t.key}`} className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {TAB_LABEL[t.key] || t.key}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {posts.state === "loading" ? (
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}</div>
            ) : posts.locked ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                <Lock size={22} className="mx-auto text-muted-foreground" />
                <p className="mt-2 font-semibold">{posts.locked === "hidden" ? "This tab isn't shown" : posts.locked === "friends" ? "Friends only" : "Private"}</p>
                <p className="mt-1 text-sm text-muted-foreground">{posts.locked === "friends" ? "Follow each other to see these posts." : "The creator keeps this section to themselves."}</p>
              </div>
            ) : posts.items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                <p className="font-semibold">Nothing here yet</p>
                <p className="mt-1 text-sm text-muted-foreground">When {p.display_name || p.handle} posts, it shows up here.</p>
              </div>
            ) : gridTabs.has(tab) ? (
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2" data-testid="profile-grid">
                {posts.pinned && !posts.items.some((x) => x.id === posts.pinned.id) && <PostTile post={posts.pinned} />}
                {posts.items.map((x) => <PostTile key={x.id} post={x} />)}
              </div>
            ) : (
              <div className="space-y-4" data-testid="profile-list">
                {posts.items.map((x) => <PostCard key={x.id} post={x} compactMode />)}
              </div>
            )}
            {posts.cursor && posts.state !== "loading" && (
              <div className="mt-5 text-center">
                <Button variant="outline" className="rounded-full px-8" disabled={posts.state === "more"} onClick={() => loadPosts(posts.cursor)}>{posts.state === "more" ? "Loading…" : "Load more"}</Button>
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          {p.book?.items?.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="profile-book">
              <h2 className="font-bold">Book {p.display_name || p.handle}</h2>
              <ul className="mt-3 space-y-2">
                {p.book.items.slice(0, 5).map((it) => {
                  const w = worldOf(it.module);
                  return (
                    <li key={it.id}>
                      <Link to={`/listing/${it.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2 text-sm hover:bg-muted/60">
                        <span className="truncate font-medium">{it.title}</span>
                        <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize" style={{ background: w.accentSoft, color: w.accent }}>{w.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {rateCard.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="profile-ratecard">
              <h2 className="font-bold">Rate card</h2>
              <ul className="mt-3 divide-y divide-border text-sm">
                {rateCard.map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 py-2">
                    <span>
                      <span className="font-medium">{r.deliverable}</span>
                      {r.turnaround && <span className="block text-xs text-muted-foreground">{r.turnaround}</span>}
                    </span>
                    <span className="font-mono-num font-semibold">{r.currency === "GBP" || !r.currency ? "£" : `${r.currency} `}{Number(r.price).toFixed(r.price % 1 ? 2 : 0)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">Collab briefs and payments run through the Fable + app.</p>
            </div>
          )}
          {Array.isArray(p.casting_listings) && p.casting_listings.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="font-bold">Open casting calls</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {p.casting_listings.slice(0, 4).map((c) => (
                  <li key={c.id}><Link to={`/listing/${c.id}`} className="font-medium hover:underline">{c.title}</Link></li>
                ))}
              </ul>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-muted/40 p-5 text-sm">
            <p className="inline-flex items-center gap-2 font-semibold"><Smartphone size={15} /> More in the app</p>
            <p className="mt-1 text-muted-foreground">Messages, Backstage, Circles, Live and collab requests are in the Fable + app.</p>
            <Link to="/get-the-app" className="mt-2 inline-block font-medium text-primary hover:underline">Get the app</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
